/**
 * The marks a catalog card wears, by the app's rules: PRO, NEW and 2D (the labels
 * FurnitureListItem.Load shows), and which cards are PRO at all.
 *
 * PRO is not a property of a model: the app marks the tail of every list. A list's first cards are
 * free and the rest PRO (FurnitureList.CheckFurniturePercentLocked, the rule while the `proItemsInRoom`
 * test is off, as it is for everyone): a card at index i of a list estimated at n models is PRO when
 * i > min(furnitureMaxUnlocked, (n − 1) · (100 − furniturePercentLocked) / 100), the index counted
 * without the NEW cards so far. Some cards are never PRO (CatalogRules.IsProFurniture): the person's
 * favourites, NEW models, models whose definition says `Free="true"`, the ForceFree tags, wall, base
 * and tall cabinets; `Free="False"` or the ForcePro tag makes a model PRO wherever it stands.
 *
 * The web has no subscription and no paywall: a PRO card is placed like any other and the mark says
 * what the app will ask for. The app's other exemptions need what the web does not have — an account
 * (the person's own models, a subscription or the free hour that hides the marks), a shop (models in
 * stock, affiliate links in the US) or the app's memory of models already placed for free — and do
 * not apply here.
 */
export interface ProSettings {
  /** `furniturePercentLocked`: the share of a list that is PRO. */
  percentLocked: number;
  /** `furnitureMaxUnlocked`: never more free cards than this, counted from the first. */
  maxUnlocked: number;
}

/**
 * The app's own copy (Assets/Resources/App/settings.xml): the same base values the server's
 * `/assets/app/settings.xml` gives on 2026-09-24 (`Base1265`: 65 and 45).
 */
export const BUNDLED_PRO_SETTINGS: ProSettings = { percentLocked: 65, maxUnlocked: 45 };

const settings: ProSettings = BUNDLED_PRO_SETTINGS;

/** The settings in force. */
export function proSettings(): ProSettings { return settings; }

/** Config.xml `/Config@ForceFree`. */
const FORCE_FREE = new Set(['KidsDecor', 'Toys', 'PlayTents', 'CeilingToys', 'ForceFree']);
/** AssetSettings.ForceProTag. */
const FORCE_PRO = 'ForcePro';
/** FurnitureInfo.IsNewForDays. */
const NEW_FOR_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

/** What the rules read about one card. */
export interface CardFacts {
  /** The model's tags: the definition's when it is read, else the catalog document's. */
  tags: string[];
  /** When it was added: the definition's `Added`, else the date the catalog published it. */
  added?: string;
  /** The definition's `Free`: true free, false PRO, absent neither. */
  free?: boolean;
  favourite?: boolean;
  /** The editor's model id (a 2D item borrows the app's model 1498). */
  id?: string;
  is2d?: boolean;
}

/** FurnitureInfo.IsNew: added in the last fortnight. */
export function isNew(facts: CardFacts, now = Date.now()): boolean {
  const time = facts.added ? Date.parse(facts.added) : NaN;
  return Number.isFinite(time) && (now - time) / DAY_MS <= NEW_FOR_DAYS;
}

/** The app's «2D» label: an Objects2D model, or one drawn on the app's 2D quad (1498). */
export function is2dCard(facts: CardFacts): boolean {
  return facts.tags.includes('Objects2D') || !!facts.id?.includes('1498');
}

/** CatalogRules.IsFreeFurniture, for what the web knows. */
export function isFreeModel(facts: CardFacts, now = Date.now()): boolean {
  if (facts.free === true || isNew(facts, now)) return true;
  if (facts.tags.some((tag) => FORCE_FREE.has(tag))) return true;
  return facts.tags.some((tag) => tag === 'WallCabinet' || tag === 'BaseCabinet' || tag === 'HighCabinet');
}

/** CatalogRules.IsForceProFurniture: PRO wherever it stands, unless it is new. */
export function isForcePro(facts: CardFacts, now = Date.now()): boolean {
  if (isNew(facts, now)) return false;
  return facts.free === false || facts.tags.includes(FORCE_PRO);
}

/**
 * FurnitureList.CheckFurniturePercentLocked without the ProItems test: whether the card at this
 * position is PRO in a list estimated at `estimate` models. `position` already leaves the NEW cards
 * out (FurnitureItemsLoader's offset).
 */
export function proByPosition(position: number, estimate: number, rule: ProSettings = settings): boolean {
  const threshold = Math.min(rule.maxUnlocked, (Math.max(estimate, 1) - 1) * (100 - rule.percentLocked) / 100);
  return position > threshold;
}

/** CatalogRules.IsProFurniture: the exemptions first, then the card's place in its list. */
export function isProCard(facts: CardFacts, byPosition: boolean, now = Date.now()): boolean {
  if (facts.favourite) return false;
  if (isFreeModel(facts, now)) return false;
  if (isForcePro(facts, now)) return true;
  return byPosition;
}

/** FurnitureCatalogOnline.PageSize: the pages the app reads the catalog in. */
const CATALOG_PAGE = 20;
/** SearchFurnitureVariations.SearchVariationsPaged: the app's gallery pages, for a list and for a search. */
export const LIST_GALLERY_PAGE = 35;
export const SEARCH_GALLERY_PAGE = 20;

/**
 * The size the app estimates a list at after each of its gallery pages, whatever pages the editor
 * itself reads in. The app reads the catalog 20 models at a time until a gallery page is full; each
 * catalog page answers its skip plus what it gave, half a page more when it gave a full one
 * (FurnitureCatalogOnline), and FurnitureItemsLoader keeps the larger of that and 0.8 of its last
 * estimate, never less than the cards it holds. `read` models are known so far; `ended` says the
 * catalog has no more, otherwise the pages not read yet count as full.
 */
export function galleryEstimates(read: number, ended: boolean, gallery: number, pages: number): number[] {
  const out: number[] = [];
  let estimate = 0, consumed = 0, skip = 0, over = false;
  for (let k = 1; k <= pages; k++) {
    const want = gallery * k;
    while (!over && consumed < want) {
      const size = skip + CATALOG_PAGE <= read ? CATALOG_PAGE : ended ? Math.max(0, read - skip) : CATALOG_PAGE;
      estimate = Math.floor(Math.max(skip + size + (size >= CATALOG_PAGE ? CATALOG_PAGE / 2 : 0), 0.8 * estimate));
      consumed = skip + size;
      skip += CATALOG_PAGE;
      if (size < CATALOG_PAGE) over = true;
    }
    estimate = Math.max(estimate, ended ? Math.min(want, read) : want);
    out.push(estimate);
  }
  return out;
}

/**
 * Which cards of a list are PRO by their place (FurnitureItemsLoader + CheckFurniturePercentLocked):
 * gallery page by gallery page, a card's index less the NEW cards up to the end of its page, against
 * that page's estimate. `fresh[i]` says the i-th card is NEW.
 */
export function proPositions(fresh: boolean[], read: number, ended: boolean, gallery: number, rule: ProSettings = settings): boolean[] {
  const pages = Math.ceil(fresh.length / gallery);
  const estimates = galleryEstimates(read, ended, gallery, pages);
  const out: boolean[] = new Array(fresh.length);
  let newSoFar = 0;
  for (let k = 0; k < pages; k++) {
    const start = k * gallery, end = Math.min(fresh.length, start + gallery);
    for (let i = start; i < end; i++) if (fresh[i]) newSoFar++;
    for (let i = start; i < end; i++) out[i] = proByPosition(i - newSoFar, estimates[k], rule);
  }
  return out;
}
